import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Input, TemplateRef, ViewChild, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { DeviceDetectorService } from 'ngx-device-detector';
import { saveAs } from 'file-saver';
import { DataManager, Query, JsonAdaptor } from '@syncfusion/ej2-data';

import {
  State, ApplicationState, getDashboardView,
  getApplicationState, getMapState, getProblems,
  getNoPin, getMarkers, getSelectedProblem,
  DashboardView, SetDashboardView, SetNoMapLocation,
  SelectProblem, SetProblemMarkers,
  DelayedAction, GetProblems, SetProblems, getNoProblems, GetMenuOption, MenuItem, getMenus,
  ProblemType, GoToProblemDetails, getAppView, SetAppView, MenuDictionaries, getDictionaries,
  SetViewLoading, getViewLoading, getProblemsFiltered, SetProblemsFiltered, SetNoProblems, SetAsset, GetDashboardView, SaveDashboardView,
  getLinkedCases, LinkedCase, LinkedCaseProblems, AddProblemLinkedCases, LinkedCasePayload, getLayerState, GetRecentProblemsList
} from '../../state-management';

import { ActivatedRoute, Router } from '@angular/router';

import {
  DnvMapState, ResizeMap, DnvMarker,
  CenterMap, ToggleMarkerActive, DnvMapAction,
  SetMarkers, SyncMarkersActive, ResetSelected, SetZoom, LayerState, SetMapFeatureLayers, MarkerClick,
  SetShowDrawControl, SetMapClusterView, SetMapShowFindMe, CenterMapZoomed, PlainMapFunctionsOnly
} from 'dnv-lib';

import { Problem } from '../../models/problem';
import { DatePipe } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';


@Component({
    selector: 'app-dashboard-component',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('linkProblemsModal') linkProblemsModal: TemplateRef<any>;

  @Input() viewLoaded: string;

  application$: Observable<ApplicationState>;
  mapState$: Observable<DnvMapState>;
  layerState$: Observable<LayerState>;
  dashboardView$: Observable<DashboardView>;
  problems$: Observable<Problem[]>;
  problemsFiltered$: Observable<Problem[]>;
  noPin$: Observable<boolean>;
  markers$: Observable<DnvMarker[]>;
  selectedProblem$: Observable<string>;
  noProblems$: Observable<boolean>;
  menuDictionaries$: Observable<MenuDictionaries>;
  menuItem$: Observable<MenuItem>;
  appView$: Observable<string>;
  viewLoading$: Observable<boolean>;
  linkedCases$: Observable<LinkedCase[]>;
  lcm$: Observable<any[]>;

  markers: DnvMarker[];
  selectedProblem = '';
  selectedMarker = '';
  oldMarker = '';
  problems: Problem[] = [];
  problemsFiltered: Problem[] = [];
  // gridData: DataManager;
  showSpinner = true;
  resetZoom = true;  // only resets onload
  hybridList = true;
  dashboardView: string;
  toggleProblem: Problem;
  menuDictionaries: MenuDictionaries;
  menuItems: MenuItem;
  intervalTimer: number;
  problemListPopulator: Function;

  linkedCases: LinkedCase[] = [];
  linkStatusHighestLevel = '';
  parentCases: LinkedCaseProblems[] = [];
  existingCases: LinkedCaseProblems[] = [];
  problemsToLink: Problem[] = [];
  linkNotesText = '';
  linkProblemToggle = false;
  shouldRefreshListViewGrid = false;
  linkedCaseExists = false;
  assignParentsToggled = true;
  forceLinkOnly = false;
  modal: NgbModalRef = null;
  isRecentsView = false;

  clearFilterSubject: Subject<void> = new Subject();
  private ngUnsubscribe: Subject<void> = new Subject();
  numLoadedLinkedCases: number;
  linkedCaseMappings: any[];

  constructor(
    private store: Store<{}>,
    private deviceService: DeviceDetectorService,
    private modalService: NgbModal,
    private router: Router
  ) {
    this.application$ = this.store.select(getApplicationState);
    this.mapState$ = this.store.select(getMapState);
    this.menuDictionaries$ = this.store.select(getDictionaries);
    this.menuItem$ = this.store.select(getMenus);
    this.dashboardView$ = this.store.select(getDashboardView);
    this.layerState$ = this.store.select(getLayerState);
    this.problems$ = this.store.select(getProblems);
    this.problemsFiltered$ = this.store.select(getProblemsFiltered);
    this.noPin$ = this.store.select(getNoPin);
    this.markers$ = this.store.select(getMarkers);
    this.selectedProblem$ = this.store.select(getSelectedProblem);
    this.noProblems$ = this.store.select(getNoProblems);
    this.appView$ = this.store.select(getAppView);
    this.viewLoading$ = this.store.select(getViewLoading);
    this.linkedCases$ = this.store.select(getLinkedCases);
  }

  ngOnDestroy() {
    clearInterval(this.intervalTimer);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit() {
    this.store.dispatch(new GetMenuOption());
    this.store.dispatch(new GetDashboardView());
    this.store.dispatch(new SetNoProblems(true));
    this.store.dispatch(new SetShowDrawControl(false));
    this.store.dispatch(new PlainMapFunctionsOnly(true));
    this.store.dispatch(new SetNoMapLocation(false));
    this.store.dispatch(new SetMapFeatureLayers([]));
    this.store.dispatch(new SelectProblem(''));
    this.store.dispatch(new ResetSelected());
    this.store.dispatch(new SetZoom(1));
    this.store.dispatch(new SetMapClusterView(false));
    this.store.dispatch(new SetMapShowFindMe(false));

    this.problemListPopulator = () => {
      this.store.dispatch(new GetProblems());
      this.store.dispatch(new SyncMarkersActive(this.selectedProblem));
      this.shouldRefreshListViewGrid = true;
    };

    this.markers$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(m => { this.markers = m; });
    this.problems$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(p => { this.problems = p; });
    this.menuDictionaries$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(m => { this.menuDictionaries = m; });
    this.menuItem$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(m => this.menuItems = m);
    this.selectedProblem$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(sp => { this.selectedProblem = sp; });
    this.linkedCases$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(linkedCases => {
      this.linkedCases = linkedCases;
      if (this.linkedCases.length && this.linkedCases.length > 0 && this.numLoadedLinkedCases !== this.linkedCases.length) {
        this.linkedCaseMappings = [];
        this.numLoadedLinkedCases = this.linkedCases.length;
        this.problems.forEach(problem => {

          const problemLinkedCases = this.linkedCases.filter(linkedCase => linkedCase.problemOneNumber === problem.problemNo);
          problemLinkedCases.forEach(problemLinkedCase => {

            if (problemLinkedCase.problemTwo_RefId && problemLinkedCase.isRelated) {
              this.linkedCaseMappings.push({
                problemNo: problem.problemNo,
                childProblemNo: problemLinkedCase.problemTwoNumber,
                priority: problemLinkedCase.problemTwo.priority,
                reportedDate: problemLinkedCase.problemTwo.reportedDate,
                problemType_Name: problemLinkedCase.problemTwo.problemType_Name,
                problemLocation: problemLinkedCase.problemTwo.problemLocation,
                problemStatus: problemLinkedCase.problemTwo.problemStatus,
                assignedTo: problemLinkedCase.problemTwo.assignedTo,
                problemRead: problemLinkedCase.problemTwo.problemRead
              });
            }
          });
        });

        if (this.linkedCaseMappings.length > 0) {
          this.lcm$ = of(this.linkedCaseMappings);
        }
      }
    });

    this.problemListPopulator();
    this.intervalTimer = setInterval(this.problemListPopulator, 300000);

    // detect device type
    if (this.deviceService.isDesktop()) {
      this.store.dispatch(new SetAppView('desktop'));
    } else if (this.deviceService.isTablet()) {
      this.store.dispatch(new SetAppView('tablet'));
    } else if (this.deviceService.isMobile()) {
      this.store.dispatch(new SetAppView('mobile'));
    }
  }

  onViewSelected(view: string) {
    this.store.dispatch(new SetViewLoading(true));
    setTimeout(() => {
      this.store.dispatch(new SetDashboardView(view));
      this.store.dispatch(new ResizeMap(true));
      this.store.dispatch(new SetNoMapLocation(false));
    }, 100);
  }

  onProblemsFilter(problemsFiltered: Problem[]) {
    this.store.dispatch(new SetProblemsFiltered(problemsFiltered));
    this.store.dispatch(new SetProblemMarkers({ problem: problemsFiltered, showToolTip: true }));
  }

  onClearFilters() {
    this.clearFilterSubject.next();
  }

  routeProblemDetail(problemNo: string) {
    this.store.dispatch(new SetNoProblems(true));
    this.router.navigateByUrl('/problemdetails?ProblemNo=' + problemNo);
  }

  onMapUpdate(action: DnvMapAction) {
    this.store.dispatch(action);
  }

  onToggleProblem(problem: Problem) {
    if (this.toggleProblem === problem) {
      this.toggleProblem = null;
    } else {
      this.toggleProblem = this.problems.filter(p => p.problemNo === problem.problemNo)[0];
    }


    this.store.dispatch(new ToggleMarkerActive(problem.problemNo));
    // if a different problem was previously selected, toggle it too
    if (this.selectedProblem.length > 0 && this.selectedProblem !== problem.problemNo) {
      this.store.dispatch(new ToggleMarkerActive(this.selectedProblem));
    }

    const problemMarker: DnvMarker = this.markers.find(m => m.id === problem.problemNo);

    // toggle 'No Map Location' notification
    if (this.selectedProblem !== problem.problemNo && problemMarker
      && (problemMarker.centerPoint.lat === 0 || problemMarker.centerPoint.lng === 0)) {
      this.store.dispatch(new SetNoMapLocation(true));
    } else {
      this.store.dispatch(new SetNoMapLocation(false));
    }

    // center map to problem latlng
    if (problemMarker && problemMarker.centerPoint.lat !== 0 && problemMarker.centerPoint.lng !== 0) {
      this.store.dispatch(new CenterMapZoomed({ centerPoint: problemMarker.centerPoint, zoomLevel: 7 }));
    }

    // this.store.dispatch(new ToggleProblemExpanded(problem.problemNo));
    if (this.selectedProblem.length === 0) {
      // open problem, nothing selected before
      this.store.dispatch(new SelectProblem(problem.problemNo));

    } else if (problem.problemNo === this.selectedProblem) {
      // close prev problem, same problem selected
      this.store.dispatch(new SelectProblem(''));

    } else {
      // open new problem, something selected before
      // close prev prob first
      //  this.store.dispatch(new ToggleProblemExpanded(this.selectedProblem));
      this.store.dispatch(new SelectProblem(problem.problemNo));
    }
  }

  onToggleHybridList() {
    this.hybridList = !this.hybridList;
  }

  onProblemNumEmit(problemNo: string) {
    this.store.dispatch(new GoToProblemDetails({ problemNo: problemNo, activityNo: '' }));
  }

  onDownload() {
    const excelProbs = Object.assign([], this.problems);
    const headerKey = ['problemNo', 'priority', 'reportedDate', 'problemType_RefId', 'problemLocation', 'problemStatus', 'assignedTo', 'parent'];
    const header = ['Case Number', 'priority', 'Reported', 'Type', 'Location', 'Status', 'Assigned To', 'Parent'];

    // add children cases to problems
    if (this.linkedCaseMappings.length > 0) {
      this.linkedCaseMappings.forEach(lcm => {
        const idx = excelProbs.findIndex(p => p.problemNo === lcm.problemNo);
        const isInList = excelProbs.findIndex(p => p.problemNo === lcm.childProblemNo);  // check if child already in list
        if (idx > -1 && isInList === -1) {
          // find lastIndex of problem no if there are already children
          const childIdx = this.findLastIndex(excelProbs, 'parent', lcm.problemNo);

          const childProb = {
            problemNo: lcm.childProblemNo,
            priority: lcm.priority,
            reportedDate: lcm.reportedDate,
            type: lcm.problemType_Name,
            problemLocation: lcm.problemLocation,
            problemStatus: lcm.problemStatus,
            assignedTo: lcm.assignedTo,
            parent: lcm.problemNo
          } as unknown as Problem;
          excelProbs.splice(childIdx > -1 ? childIdx + 1 : idx + 1, 0, childProb);
        }

      });
    }

    const replacer = (key: string, value: any) => (value === null ? '' : value);
    const csv = excelProbs.map((row) =>
      headerKey
        .map((fieldName: string) =>
          JSON.stringify(this.getValue(fieldName, row), replacer))
        .join(',')
    );

    csv.unshift(header.join(','));
    const csvArray = csv.join('\r\n');

    const blob = new Blob([csvArray], { type: 'text/csv' });
    const pipe = new DatePipe('en-CA');
    const dateStr = pipe.transform(new Date(), 'yyyyMMddhhmmss');
    // console.log(csvArray);
    saveAs(blob, 'dashboard-export-' + dateStr + '.csv');
  }

  onSaveDefaultView(setting: string) {
    this.store.dispatch(new SaveDashboardView(setting));
  }

  getValue(key: any, obj: Problem) {
    const pipe = new DatePipe('en-CA');
    if (key === 'problemType_RefId') {
      const probType = this.menuDictionaries.problemTypes.get(obj[key]);
      if (!probType && obj.type !== '') { // child problem
        return obj.type;
      }
      return probType !== null ? probType.problemType_Name : '';
    } else if (key === 'reportedDate') {
      return pipe.transform(obj[key], 'yyyy-MM-dd');
    } else if (key === 'assignedTo') {
      return obj[key] === '' ? 'Unassigned' : obj[key];
    } else {
      return obj[key];
    }
  }

  onViewLoaded(viewLoaded: boolean) {
    this.store.dispatch(new SetViewLoading(false));
  }

  onLinkProblemsToggle(linkProblemsToggleState: boolean) {
    // Used to pass data between the list and nav dashboard components -> To indicate whether or not to show the checkboxes
    this.linkProblemToggle = linkProblemsToggleState;
  }

  onProblemsToLinkSelected(problemsToLink: Problem[]) {
    // Used to pass data between the list and nav dashboard components -> To indicate when to show the continue button
    this.problemsToLink = problemsToLink;
  }

  updateListViewGridState(listViewRefreshState: boolean) {
    this.shouldRefreshListViewGrid = listViewRefreshState;
  }

  handleAssignRadioClick(event: any) {
    if (event.target.checked) {
      if (event.target.value === '1') {
        this.assignParentsToggled = true;
      } else {
        this.assignParentsToggled = false;
      }
    }
  }

  convertCaseNoToURL(problemNo: string): string {
    const webRoot = `https://${window.location.host}`;
    const problemURL = `${webRoot}/problemdetails?ProblemNo=${problemNo}`;
    return problemURL;
  }

  openModal(modal: TemplateRef<any>) {
    this.modal = this.modalService.open(modal, { backdrop: 'static' });
  }

  closeModal() {
    if (this.modal) {
      this.modal.close();
    }
  }

  showTooltip() {
    (document.getElementsByClassName('tooltipText') as any)[0].style.visibility = 'visible';
    (document.getElementsByClassName('tooltipArrow') as any)[0].style.visibility = 'visible';
  }

  hideToolTip() {
    (document.getElementsByClassName('tooltipText') as any)[0].style.visibility = 'hidden';
    (document.getElementsByClassName('tooltipArrow') as any)[0].style.visibility = 'hidden';
  }

  getLinkedCaseStatus(linkedCase: LinkedCase, problem: Problem) {
    if (linkedCase) {
      if (!linkedCase.isRelated) {
        return 'Related';
      }
    }

    return problem.linkedStatus;
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(evt: KeyboardEvent) {
    if (this.modal) {
      this.closeLinkProblemPrompt();
    }
  }

  onLinkProblems() {
    this.linkStatusHighestLevel = 'Unlinked';

    // Determine whether a linked case exists between selected members
    this.problemsToLink.forEach(problem => {
      const linkedCaseEntry = this.linkedCases.filter(linkedCase =>
        linkedCase.problemOneNumber === problem.problemNo ||
        linkedCase.problemTwoNumber === problem.problemNo);

      this.problemsToLink.forEach(prbCompare => {
        linkedCaseEntry.forEach(linkedCase => {
          const linkExists = (
            (linkedCase.problemOneNumber === problem.problemNo && linkedCase.problemTwoNumber === prbCompare.problemNo) ||
            (linkedCase.problemOneNumber === prbCompare.problemNo && linkedCase.problemTwoNumber === problem.problemNo)
          );

          if (linkExists) {
            this.linkedCaseExists = true;
            const indexCase = this.existingCases.findIndex(entry => entry.problemOne.problemNo === linkedCase.problemOneNumber);

            if (indexCase !== -1) {
              const caseProbTwoExists = this.existingCases[indexCase].problemTwoArr.some(
                problemCase => problemCase.problemNo === linkedCase.problemTwoNumber
              );

              if (!caseProbTwoExists) {
                this.existingCases[indexCase].problemTwoArr.push(linkedCase.problemTwo);
              }
            } else {
              this.existingCases.push({
                problemOne: linkedCase.problemOne,
                problemTwoArr: [linkedCase.problemTwo],
                linkedCase: linkedCase
              });
            }
          }
        });
      });
    });

    // Determine the highest linkedCase status
    if (!this.linkedCaseExists) {
      this.problemsToLink.forEach(problem => {

        const linkedCaseEntry = this.linkedCases.filter(linkedCase =>
          linkedCase.problemOneNumber === problem.problemNo ||
          linkedCase.problemTwoNumber === problem.problemNo);

        if (linkedCaseEntry.length) {
          linkedCaseEntry.forEach(entry => {
            // handle the parent entries
            if (entry.problemOneNumber === problem.problemNo && entry.problemOne.linkedStatus === 'Parent') {
              this.linkStatusHighestLevel = 'Parent';
              const parentCaseIndex = this.parentCases.findIndex(
                parentCase => parentCase.problemOne.problemNo === entry.problemOne.problemNo
              );

              // No parent case exists yet
              if (parentCaseIndex === -1) {
                this.parentCases.push({
                  problemOne: entry.problemOne,
                  problemTwoArr: entry.problemTwo_RefId ? [entry.problemTwo] : []
                });
              } else {
                if (entry.problemTwo_RefId) {
                  this.parentCases[parentCaseIndex].problemTwoArr.push(entry.problemTwo);
                }
              }
            }
          });
        }
      });
    }

    clearInterval(this.intervalTimer);
    this.openModal(this.linkProblemsModal);
  }

  linkProblems() {
    let selectedRelationship = '';
    const newLinkedCases: LinkedCasePayload[] = [];

    // Determine what relationship between the selected problems and problemOne will be like
    switch (this.linkStatusHighestLevel) {
      case 'Unlinked':
        selectedRelationship = this.assignParentsToggled ?
          (<HTMLInputElement>document.querySelector('input[name="linkedCaseSelection"]:checked')).value : 'linkOnly';
        break;

      case 'Parent':
        selectedRelationship = this.assignParentsToggled && this.parentCases.length === 1 ?
          this.parentCases[0].problemOne.problemNo : 'linkOnly';
        break;

      default:
        selectedRelationship = 'linkOnly';
    }

    // Case 1: selectedRelationship set to linkOnly
    if (selectedRelationship === 'linkOnly') {

      // Go through each problems and create a link to each of the selected problems
      this.problemsToLink.forEach(problemOne => {
        this.problemsToLink.forEach(problemTwo => {
          if (problemOne.problemNo !== problemTwo.problemNo) {
            const linkEntryCreated = newLinkedCases.some(entry => (
              (entry.problemOneNumber === problemOne.problemNo && entry.problemTwoNumber === problemTwo.problemNo) ||
              (entry.problemOneNumber === problemTwo.problemNo && entry.problemTwoNumber === problemOne.problemNo)
            ));

            if (!linkEntryCreated) {
              newLinkedCases.push({
                application: 'RAP',
                notes: this.linkNotesText,
                isRelated: false,
                problemOneNumber: problemOne.problemNo,
                problemTwoNumber: problemTwo.problemNo
              });
            }
          }
        });
      });
    } else {
      // Case 2: selectedRelationship set to the selected problem to be a parent
      const problemOneNumber = selectedRelationship;
      const problemTwoCases = this.problemsToLink.filter(problem => problem.problemNo !== problemOneNumber);

      problemTwoCases.forEach(problem => {
        newLinkedCases.push({
          application: 'RAP',
          notes: this.linkNotesText,
          isRelated: true,
          problemOneNumber: problemOneNumber,
          problemTwoNumber: problem.problemNo
        });
      });
    }

    // handle dispatch of linked case event
    this.store.dispatch(new AddProblemLinkedCases(newLinkedCases));
    this.closeLinkProblemPrompt();
    this.resetCheckBoxes();
  }

  closeLinkProblemPrompt() {
    this.closeModal();

    this.parentCases = [];
    this.existingCases = [];
    this.linkNotesText = '';
    this.assignParentsToggled = true;
    this.linkedCaseExists = false;
    this.forceLinkOnly = false;
    this.intervalTimer = setInterval(this.problemListPopulator, 60000);
  }

  resetCheckBoxes() {
    const checkBoxElements = document.querySelectorAll('input[type="checkbox"]');
    checkBoxElements.forEach((element: HTMLInputElement) => {
      if (element.checked) {
        element.click();
      }
    });

    setTimeout(() => {
      const cancelLinkProblemBtn: any = document.querySelector('button[title="Cancel Link Problems"]');
      cancelLinkProblemBtn.click();
    }, 250);

    // reset problemsToLink here as we only want this list to be empty when the selected members is 0
    this.problemsToLink = [];
  }

  findLastIndex(array: any[], searchKey: string | number, searchValue: any) {
    const index = array.slice().reverse().findIndex(x => x[searchKey] === searchValue);
    const count = array.length - 1;
    const finalIndex = index >= 0 ? count - index : index;
    // console.log(finalIndex);
    return finalIndex;
  }
}

